import { LitElement, html, css, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import '@vitesse/base-ui/elements/vitesse-dialog';
import '@vitesse/base-ui/elements/vitesse-button';
import './vitesse-schema-form';
import '@vitesse/base-ui/elements/inputs/vitesse-select';
import '@vitesse/base-ui/elements/inputs/vitesse-text-input';
import { statusBadge, statusBadgeStyles, type BadgeColor } from '@vitesse/base-ui/components/templates';
import { getSession } from '@vitesse/base-ui';
import { t, LocaleController } from '@vitesse/base-ui/i18n';
import type { IntegrationCatalogType } from '../../core/bridge/connect-client';
import type { Integration } from '../../core/domain/integration';
import type { BackendClient } from '../bridge/backend-client';
import { seedValues, applyRequest } from '../components/integration/integration-form-model';
import { isKind, rooms, requirementFor, usageFor, shortHint, type Room } from '../components/integration/integration-limits';

/**
 * <vitesse-add-integration-dialog> — the catalog-driven Add Integration flow (the
 * 2nd integration API). Mirrors <vitesse-add-controller-dialog>: it loads the
 * type catalog the integrations service publishes, picks a type, renders that
 * type's connection form (FieldSpec) via the SAME <vitesse-schema-form>, and
 * submits Integrations.Apply. NO per-type UX is hardcoded — the form is the
 * catalog. "Test" validates the connection without persisting (validate_only);
 * "Add" persists. On success the roster stream delivers the new integration, so
 * the dialog just closes.
 *
 *   <vitesse-add-integration-dialog .open=${open} .gatewayId=${gw} .backend=${b}
 *       @dialog-close=${() => (open = false)}></vitesse-add-integration-dialog>
 */
@customElement('vitesse-add-integration-dialog')
export class VitesseAddIntegrationDialog extends LitElement {
  @property({ type: Boolean }) open = false;
  @property() gatewayId = '';
  @property({ attribute: false }) backend?: BackendClient;
  /** When set, the dialog is in EDIT mode: pre-fills this integration's type +
   *  name + connection fields, locks the type, and re-applies under its existing
   *  id (the secret stays blank — the service preserves the current one). */
  @property({ attribute: false }) editIntegration?: Integration;
  /** The gateway's roster — what is already running, so the picker can say
   *  which kinds the plan has no room left for BEFORE anything is typed. */
  @property({ attribute: false }) integrations: readonly Integration[] = [];

  @state() private types: IntegrationCatalogType[] = [];
  // A plan change (an upgrade landing mid-dialog) re-greys the picker.
  private sessionOff?: () => void;
  @state() private loading = false;
  @state() private typeKey = '';
  @state() private displayName = '';
  /* Has the user made the name THEIRS? Until they do, the name field tracks the
   * chosen type, so switching MQTT → PostgreSQL renames "MQTT" to "PostgreSQL"
   * instead of leaving a lie behind. Once they type, it is their name and no type
   * change may touch it. Editing an existing integration counts as touched from
   * the start — its name is already the user's. */
  private nameTouched = false;
  @state() private values: Record<string, string> = {};
  // The Test/Add outcome, rendered as the shared BASE status badge pinned to the
  // footer's LEFT (vertically centered with the buttons) rather than as a line in
  // the body — so a result appearing/clearing never shifts the form vertically.
  @state() private status: { color: BadgeColor; label: string } | null = null;
  @state() private busy = false;

  constructor() {
    super();
    new LocaleController(this); // every string here is t()'d — re-render on a switch
  }

  static styles = [
    statusBadgeStyles, // the shared BASE status badge — reused, not re-implemented
    css`
      .form-group {
        margin-bottom: 16px;
      }
      .form-group label {
        display: block;
        margin-bottom: 8px;
        font-size: 14px;
        font-weight: 500;
        color: var(--text-primary);
      }
      .empty {
        margin: 0;
        font-size: 13px;
        color: var(--text-secondary);
      }
      .empty.limit {
        margin-top: 8px;
        color: var(--text-primary);
      }
      /* Footer POSITIONING only (the badge's own look comes from statusBadgeStyles):
         margin-right:auto pins it to the LEFT while the action buttons stay right
         (footer is justify-content:flex-end); align-self centers it against the
         taller buttons; max-width + the badge label's nowrap keep a long error from
         ballooning the footer (full text is on the title tooltip). */
      .footer-status {
        align-self: center;
        margin-right: auto;
        min-width: 0;
        max-width: 58%;
        overflow: hidden;
      }
      .footer-status .base-status-badge {
        max-width: 100%;
      }
      .footer-status .base-status-badge-label {
        overflow: hidden;
        text-overflow: ellipsis;
      }
    `,
  ];

  connectedCallback(): void {
    super.connectedCallback();
    this.sessionOff = getSession()?.onChange(() => this.requestUpdate());
  }
  disconnectedCallback(): void {
    this.sessionOff?.();
    this.sessionOff = undefined;
    super.disconnectedCallback();
  }

  // Load the catalog each time the dialog opens (it's small + per-gateway).
  willUpdate(changed: Map<string, unknown>): void {
    if (changed.has('open') && this.open) void this.onOpen();
  }

  /*
   * What the plan has room for, per kind — the whole reason the picker can grey
   * a type. Computed at render from the roster + the session's plan; the rules
   * (an absent ceiling is UNLIMITED, never zero) live in integration-limits.ts.
   * In EDIT mode nothing is full: a re-apply under the same id is not counted
   * against the org by the server either.
   */
  private roomOf(t: IntegrationCatalogType): Room | undefined {
    if (this.editIntegration || !isKind(t.viewKind)) return undefined;
    const s = getSession();
    return rooms(this.integrations, (f, k) => s?.limit(f, k))[t.viewKind];
  }
  private fullReason(r: Room): string {
    return getSession()?.why(requirementFor(r.kind), usageFor(r)) ?? shortHint(r);
  }

  private async onOpen(): Promise<void> {
    this.reset();
    if (!this.backend || !this.gatewayId) return;
    this.loading = true;
    try {
      // Alphabetical by display name so the type dropdown is scannable (the catalog
      // publish order is arbitrary). A fresh copy — never sort the manager's array.
      const catalog = await this.backend.api.getIntegrationCatalog(this.gatewayId);
      this.types = [...catalog].sort((a, b) => a.displayName.localeCompare(b.displayName));
      const ed = this.editIntegration;
      // Land on the first type the plan still has room for — never on one whose
      // only possible outcome is a refusal after the form is filled in.
      const initialType = ed?.type ?? (this.types.find((t) => !this.roomOf(t)?.full) ?? this.types[0])?.key ?? '';
      if (initialType) this.selectType(initialType);
      if (ed) {
        // Pre-fill from the current integration (secrets are stripped → blank).
        // Touched: an existing name is the user's, so the (locked) type never
        // rewrites it.
        this.displayName = ed.displayName;
        this.nameTouched = true;
        this.values = { ...this.values, ...ed.fields };
      }
    } catch (err) {
      this.status = { color: 'red', label: err instanceof Error ? err.message : String(err) };
    } finally {
      this.loading = false;
    }
  }

  private selected(): IntegrationCatalogType | undefined {
    return this.types.find((t) => t.key === this.typeKey);
  }

  // Selecting a type seeds every field's default so a submit carries the whole form
  // (a SELECT seeds to its first option — see seedValues — so `auth` starts "none").
  private selectType(key: string): void {
    this.typeKey = key;
    const t = this.selected();
    this.values = t ? seedValues(t.form) : {};
    // The name follows the type until the user claims it — so the default name of
    // a new MQTT integration is "MQTT", and a roster is self-labelling from the
    // first one created. Their own name is never overwritten.
    if (!this.nameTouched) this.displayName = t?.displayName ?? '';
    this.status = null;
  }

  private reset(): void {
    this.types = [];
    this.typeKey = '';
    /*
     * Blank, because selectType() fills it with the chosen type's name a moment
     * later (onOpen: reset → selectType) and edit mode overwrites it with the
     * integration's real name.
     *
     * It used to seed the literal "My Integration" here, and that string then
     * became the name of nearly every integration anyone created — the field was
     * never blank, so the sensible `|| t.displayName` fallback in buildReq() could
     * never fire. A roster of nine cards all reading "My Integration" is a naming
     * problem, not a card problem, and it is worth fixing at the source: the name
     * is what every list, dropdown, breadcrumb and log line shows.
     */
    this.displayName = '';
    this.nameTouched = false;
    this.values = {};
    this.status = null;
    this.busy = false;
  }

  private close(): void {
    this.dispatchEvent(new CustomEvent('dialog-close', { bubbles: true, composed: true }));
  }

  // Split the one values map into plaintext fields vs. secrets (by the server's
  // flag — the service seals the secrets), dropping whatever the chosen mode
  // hides. An edit sends only what the dialog shows, under a field mask, so the
  // Forwards, operations and description stay as stored. The rules live in
  // integration-form-model.ts where they are tested. The id is derived from the
  // name (readable + collision-resistant); a fresh id ADDs.
  private buildReq(validateOnly: boolean) {
    const t = this.selected()!;
    // Edit re-applies under the SAME id; add derives a fresh readable id.
    let integrationId: string;
    if (this.editIntegration) {
      integrationId = this.editIntegration.id;
    } else {
      const slug =
        (this.displayName || t.key)
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '')
          .slice(0, 32) || t.key;
      integrationId = `${slug}-${Math.random().toString(36).slice(2, 6)}`;
    }
    return applyRequest(
      integrationId,
      !!this.editIntegration,
      t.key,
      this.displayName || t.displayName,
      t.form,
      this.values,
      validateOnly,
    );
  }

  private async run(validateOnly: boolean): Promise<void> {
    const type = this.selected(); // not `t` — that is the translator
    if (!type || !this.backend || this.busy) return;
    this.busy = true;
    this.status = {
      color: 'yellow',
      label: validateOnly
        ? t('integrationDialog.testingConnection')
        : this.editIntegration
          ? t('integrationDialog.saving')
          : t('integrationDialog.adding'),
    };
    try {
      const res = await this.backend.api.applyIntegration(this.gatewayId, this.buildReq(validateOnly));
      if (res.ok) {
        if (validateOnly) this.status = { color: 'green', label: t('integrationDialog.testOk') };
        else this.close(); // success → the roster stream delivers the new integration
        return;
      }
      // The server's sentence wins when there is one — it names the field to fix.
      this.status = {
        color: 'red',
        label: res.error || (validateOnly ? t('integrationDialog.testFailed') : t('integrationDialog.addFailed')),
      };
    } catch (err) {
      this.status = { color: 'red', label: err instanceof Error ? err.message : String(err) };
    } finally {
      this.busy = false;
    }
  }

  render() {
    const type = this.selected(); // not `t` — that is the translator now
    const editing = !!this.editIntegration;
    // The selected type's room, when it is full — reachable only if EVERY type
    // is full (onOpen skips full ones), and then the reason is shown in place so
    // nobody has to open the dropdown to learn why Add is disabled.
    const selectedRoom = type ? this.roomOf(type) : undefined;
    const blocked = !!selectedRoom?.full;
    return html`<vitesse-dialog
      .open=${this.open}
      heading=${editing ? t('integrationDialog.editHeading') : t('integrationDialog.addHeading')}
      @dialog-close=${this.close}
    >
      ${this.loading
        ? html`<p class="empty">${t('integrationDialog.loadingTypes')}</p>`
        : this.types.length === 0
          ? html`<p class="empty">${t('integrationDialog.noTypes')}</p>`
          : html`
              <div class="form-group">
                <label>${t('integrationDialog.typeLabel')}</label>
                <vitesse-select
                  .options=${this.types.map((x) => {
                    // A full kind stays LISTED, greyed, with how full: hiding it
                    // would mean a customer never learns the type exists to be
                    // bought. The row says "1 of 1 database in use"; the tooltip
                    // carries the full sentence, the one the server would answer.
                    const r = this.roomOf(x);
                    return r?.full
                      ? { value: x.key, label: x.displayName, disabled: true, hint: shortHint(r), title: this.fullReason(r) }
                      : { value: x.key, label: x.displayName };
                  })}
                  .value=${this.typeKey}
                  .disabled=${editing}
                  @change=${(e: CustomEvent) => this.selectType(String((e.detail as { value: string }).value))}
                ></vitesse-select>
                ${blocked && selectedRoom ? html`<p class="empty limit">${this.fullReason(selectedRoom)}</p>` : nothing}
              </div>
              <div class="form-group">
                <label>${t('integrationDialog.nameLabel')}</label>
                <vitesse-text-input
                  .value=${this.displayName}
                  placeholder=${type?.displayName ?? t('integrationDialog.namePlaceholder')}
                  @change=${(e: CustomEvent) => {
                    this.displayName = String((e.detail as { value: string }).value);
                    // Any edit — including clearing it — makes the name theirs, so
                    // a later type change stops rewriting it. buildReq still falls
                    // back to the type name if they left it empty.
                    this.nameTouched = true;
                  }}
                ></vitesse-text-input>
              </div>
              ${type
                ? html`<vitesse-schema-form
                    heading=${t('integrationGroup.connection')}
                    .fields=${type.form}
                    .values=${this.values}
                    @form-change=${(e: CustomEvent) =>
                      (this.values = (e.detail as { values: Record<string, string> }).values)}
                  ></vitesse-schema-form>`
                : nothing}
              ${editing
                ? html`<p class="empty">${t('integrationDialog.keepSecretHint')}</p>`
                : nothing}
            `}
      ${this.status
        ? html`<span slot="footer" class="footer-status" title=${this.status.label}
            >${statusBadge(this.status.color, this.status.label)}</span
          >`
        : nothing}
      <vitesse-button slot="footer" color="red" @click=${this.close}>${t('integrationDialog.cancel')}</vitesse-button>
      <vitesse-button slot="footer" color="yellow" ?disabled=${this.busy || !type || blocked} @click=${() => this.run(true)}>
        ${this.busy ? t('integrationDialog.testing') : t('integrationDialog.test')}
      </vitesse-button>
      <vitesse-button slot="footer" color="blue" ?disabled=${this.busy || !type || blocked} @click=${() => this.run(false)}>
        ${this.busy
          ? editing
            ? t('integrationDialog.saving')
            : t('integrationDialog.adding')
          : editing
            ? t('integrationDialog.save')
            : t('integrationDialog.add')}
      </vitesse-button>

    </vitesse-dialog>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'vitesse-add-integration-dialog': VitesseAddIntegrationDialog;
  }
}
